import {
  MAX_SECURITY_CONTROL_SCOPE,
  selectSecurityControlAggregates,
  selectSecurityControlDefaultScope,
  selectSecurityControlEnvironments,
} from "@app/deploy/security-controls";
import {
  selectOrganizationById,
  selectOrganizationSelectedId,
} from "@app/organizations";
import { useSelector } from "@app/react";
import {
  securityControlDetailsUrl,
  securityControlImplementationUrl,
} from "@app/routes";
import { Tab } from "@app/ui/shared";
import { IconX } from "@app/ui/shared/icons";
import type { ScopeEnvironment } from "@app/ui/shared/security-controls";
import { useCallback, useEffect, useMemo } from "react";
import {
  Outlet,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";

export interface SecurityControlDetailContext {
  controlId: string;
  orgId: string;
  organizationName: string;
  organizationSsoEnforced: boolean;
  control: ReturnType<
    typeof selectSecurityControlAggregates
  >["controls"][number];
  scopeEnvironments: ScopeEnvironment[];
  scopeIds: string[];
}

const toScopeIds = (scope: string | null, fallback: string[]) => {
  if (!scope) return fallback;
  return scope
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean)
    .slice(0, MAX_SECURITY_CONTROL_SCOPE);
};

export const SecurityControlDetailLayout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { controlId = "" } = useParams<{ controlId: string }>();
  const orgId = useSelector(selectOrganizationSelectedId);
  const organization = useSelector((state) =>
    selectOrganizationById(state, { id: orgId }),
  );
  const envSets = useSelector((state) =>
    selectSecurityControlEnvironments(state, { orgId }),
  );
  const defaultScope = useSelector((state) =>
    selectSecurityControlDefaultScope(state, { orgId }),
  );

  const scopeIds = toScopeIds(searchParams.get("scope"), defaultScope);
  const aggregates = useSelector((state) =>
    selectSecurityControlAggregates(state, { orgId, scopeIds }),
  );
  const control = useMemo(
    () => aggregates.controls.find((c) => c.id === controlId),
    [aggregates.controls, controlId],
  );

  const scopeEnvironments: ScopeEnvironment[] = scopeIds
    .map((id) => {
      const env = envSets.all.find((candidate) => candidate.id === id);
      if (!env) return undefined;
      return {
        id: env.id,
        handle: env.handle,
        type: env.type,
        stackId: env.stackId,
      };
    })
    .filter((env): env is ScopeEnvironment => Boolean(env));

  const searchString = searchParams.toString();
  const handleClose = useCallback(() => {
    const params = new URLSearchParams(searchString);
    navigate(`/controls?${params.toString()}`);
  }, [navigate, searchString]);

  useEffect(() => {
    if (!control) {
      handleClose();
    }
  }, [control, handleClose]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleClose]);

  if (!control) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 bg-black/30"
        aria-label="Close security control details"
        onClick={handleClose}
      />
      <aside className="fixed inset-y-0 right-0 z-50 w-full max-w-4xl overflow-y-auto bg-white shadow-2xl">
        <div className="px-8 pt-6 pb-0">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                {control.globalControl.area}
              </p>
              <h2 className="mt-1 text-2xl font-semibold text-gray-900">
                {control.globalControl.name}
              </h2>
              {control.globalControl.description ? (
                <p className="mt-3 max-w-3xl text-sm text-gray-600">
                  {control.globalControl.description}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="ml-4 shrink-0 rounded p-1 transition-colors hover:bg-gray-100"
              aria-label="Close"
            >
              <IconX className="w-5 h-5 text-gray-400 hover:text-gray-600" />
            </button>
          </div>

          <nav className="mt-4 flex space-x-8 border-b border-black-100">
            <Tab
              to={securityControlImplementationUrl(controlId, searchParams)}
              label="Implementation"
            />
            <Tab
              to={securityControlDetailsUrl(controlId, searchParams)}
              label="Details"
            />
          </nav>
        </div>

        <div className="px-8 py-6">
          <Outlet
            context={{
              controlId,
              orgId,
              organizationName: organization.name,
              organizationSsoEnforced: organization.ssoEnforced,
              control,
              scopeEnvironments,
              scopeIds,
            }}
          />
        </div>
      </aside>
    </>
  );
};

export const useSecurityControlDetailContext = () =>
  useOutletContext<SecurityControlDetailContext>();
